import { useMemo } from 'react';

import { ASSET_TYPES, CERTIFICATION_COLORS } from '@/shared/api/openmetadata';
import { TAG_FQN_PREFIXES } from '@/shared/lib/constants';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { getCertificationFromFqn } from '@/shared/lib/format/fqn';

import type { AssetBaseInfo } from '@/features/asset-detail/api';

type AssetSummaryDisplayProps = {
  assetData: AssetBaseInfo | null;
  entityType: string;
};

export const useAssetSummaryDisplay = ({ assetData, entityType }: AssetSummaryDisplayProps) => {
  const ownerDisplay = useMemo(() => {
    if (!assetData?.owners || assetData.owners.length === 0) {
      return { text: '없음', isTeam: false, count: 0 };
    }

    const teamOwner = assetData.owners.find((owner) => owner.type === 'team');
    if (teamOwner) {
      return {
        text: teamOwner.displayName || teamOwner.name,
        isTeam: true,
        count: 1,
      };
    }

    const userOwners = assetData.owners.filter((owner) => owner.type === 'user');
    if (userOwners.length === 1) {
      return {
        text: userOwners[0].displayName || userOwners[0].name,
        isTeam: false,
        count: 1,
      };
    } else if (userOwners.length > 1) {
      return {
        text: `${userOwners[0].displayName || userOwners[0].name} 외 ${userOwners.length - 1}명`,
        isTeam: false,
        count: userOwners.length,
      };
    }

    return { text: '없음', isTeam: false, count: 0 };
  }, [assetData]);

  const serviceName = useMemo(() => {
    return assetData?.service?.displayName || assetData?.service?.name || '없음';
  }, [assetData]);

  const tierDisplay = useMemo(() => {
    let tierFQN = '';
    let tierName = '';

    if (assetData?.tier?.tagFQN) {
      tierFQN = assetData.tier.tagFQN;
      tierName = assetData.tier.name;
    } else {
      const tierTag = assetData?.tags?.find((t) => t.tagFQN.startsWith(TAG_FQN_PREFIXES.TIER));
      if (tierTag) {
        tierFQN = tierTag.tagFQN;
        tierName = tierTag.name;
      }
    }

    if (tierFQN && tierName) {
      return { tierName, tierFQN };
    }

    return null;
  }, [assetData]);

  const certificationDisplay = useMemo(() => {
    const isDatabaseEntity =
      entityType === ASSET_TYPES.TABLE ||
      entityType === ASSET_TYPES.DATABASE ||
      entityType === ASSET_TYPES.DATABASE_SCHEMA;

    if (!isDatabaseEntity) return null;

    const fqn = assetData?.fullyQualifiedName;
    if (!fqn) return null;

    return getCertificationFromFqn(fqn, CERTIFICATION_COLORS);
  }, [assetData, entityType]);

  const domainsDisplay = useMemo(() => {
    if (assetData?.domains && assetData.domains.length > 0) {
      return assetData.domains.map((d) => d.displayName || d.name).join(', ');
    }
    return '–';
  }, [assetData]);

  const updatedAt = useMemo(() => {
    if (assetData?.updatedAt) {
      return formatDateTime(assetData.updatedAt);
    }
    return '–';
  }, [assetData]);

  const rowCount = useMemo(() => {
    const count = assetData?.rowCount ?? assetData?.profile?.rowCount;
    if (count != null) {
      return count.toLocaleString();
    }
    return null;
  }, [assetData]);

  const tableCount = useMemo(() => {
    if (entityType === ASSET_TYPES.DATABASE_SCHEMA) {
      return null;
    }
    if (entityType === ASSET_TYPES.DATABASE && assetData?.databaseSchemas) {
      return assetData.databaseSchemas.length.toLocaleString();
    }
    if (entityType === ASSET_TYPES.CONTAINER && assetData?.numberOfObjects) {
      return assetData.numberOfObjects.toLocaleString();
    }
    return null;
  }, [entityType, assetData]);

  return {
    ownerDisplay,
    serviceName,
    tierDisplay,
    certificationDisplay,
    domainsDisplay,
    updatedAt,
    rowCount,
    tableCount,
  };
};
